import { getFormAsState } from '@brightdrop/bd-form';
import {
  formatZoneName,
  resolvePreferences,
} from '@brightdrop/localization-core';
import { isBoolean } from 'lodash-es';
import type { RuleItemResponse } from '~/api/generated-api';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { TIMEZONE_NEW_YORK } from '~/common/constants/common.constant';
import {
  AlertCategory,
  AlertEvent,
  type AvailableSubscription,
  DiagnosticEvent,
  type SubscriptionEvent,
} from '~/common/models/alert.model';
import type {
  NotificationCardFormFields,
  NotificationFormState,
} from '~/common/models/notification.model';
import {
  type CamerasSetting,
  CamerasSettingType,
  EmailFrequency,
} from '~/common/models/settings.model';
import {
  getDiagnosticTypeKey,
  getVehicleHealthCopyKeys,
} from '~/common/utils/vehicleHealthCopy';
import { iconMap } from '~/routes/notifications/constants';
import {
  PROFILE_SUBSCRIPTION_QUERY,
  type TimeWithTimezone,
  VEHICLE_ACTIVITY_SUBSCRIPTION_QUERY,
  VEHICLE_DIAGNOSTICS_SUBSCRIPTION_QUERY,
  VEHICLE_IDLING_DEFAULT_DURATION,
  VEHICLE_INACTIVITY_DEFAULT_DURATION,
  VEHICLE_OFF_HOURS_DEFAULT_DAYS,
  VEHICLE_OFF_HOURS_DEFAULT_END_TIME,
  VEHICLE_OFF_HOURS_DEFAULT_START_TIME,
} from './settings.constant';
import type { NOTIFICATION_SETTINGS_MESSAGES } from './settings.messages';

export const getEditNotificationCardState = (
  notification?: Partial<AvailableSubscription>
): NotificationFormState => {
  return getFormAsState({
    name: notification?.preferredName || notification?.name || null,
    receiptPreferences: notification?.receiptPreferences || null,
    selectedEvents:
      notification?.eventOptions?.sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      ) || null,
    selectedAssets: notification?.selectedAssets || null,
    selectedVehicleSubscriberType:
      notification?.selectedVehicleSubscriberType || null,
    active: notification?.active ?? true,
  } as NotificationCardFormFields);
};

export const getSubscriptionsQuery = (category: string) => {
  switch (category) {
    case AlertCategory.ASSET:
      return VEHICLE_ACTIVITY_SUBSCRIPTION_QUERY;
    case AlertCategory.DIAGNOSTIC:
      return VEHICLE_DIAGNOSTICS_SUBSCRIPTION_QUERY;
    case AlertCategory.PROFILE: {
      return PROFILE_SUBSCRIPTION_QUERY;
    }
    default:
      return VEHICLE_DIAGNOSTICS_SUBSCRIPTION_QUERY;
  }
};

export const getDiagnosticEventName = (
  event: DiagnosticEvent,
  labels: typeof NOTIFICATION_SETTINGS_MESSAGES,
  isVehicleHealthEnabled = false
) => {
  const key = getDiagnosticTypeKey(event, isVehicleHealthEnabled);
  return labels?.diagnostics?.[key as keyof typeof labels.diagnostics] || event;
};

/**
 * Canonicalizes the two backend battery events (`low-battery` and the newer
 * `twelve-volt-battery`) into the single `low-battery` the UI renders, so either
 * event - or both, in any order - yields one checkbox.
 * @param isVehicleHealthEnabled - When off, `twelve-volt-battery` is dropped entirely.
 */
export const collapseTwelveVoltBatteryEvents = <
  T extends { event: string; subTypes?: string[] | null },
>(
  events: T[],
  isVehicleHealthEnabled: boolean
): T[] => {
  // Dropped, not passed through: with the flag off it has no label and would be saved back.
  if (!isVehicleHealthEnabled) {
    return events.filter(
      (item) => item.event !== DiagnosticEvent.TWELVE_VOLT_BATTERY
    );
  }
  // Hoisted so the output is identical whichever order the backend sends the two events in.
  const lowBatteryEvent = events.find(
    (item) => item.event === DiagnosticEvent.LOW_BATTERY
  );
  const seen = new Set<string>();
  return events.reduce<T[]>((acc, item) => {
    const isTwelveVolt = item.event === DiagnosticEvent.TWELVE_VOLT_BATTERY;
    const event = isTwelveVolt ? DiagnosticEvent.LOW_BATTERY : item.event;
    if (seen.has(event)) {
      return acc;
    }
    seen.add(event);
    if (!isTwelveVolt) {
      acc.push(item);
      return acc;
    }
    // Sub-types belong to twelve-volt-battery only, and are re-attached on the way out.
    const { subTypes: _subTypes, ...withoutSubTypes } = item;
    acc.push(lowBatteryEvent ?? ({ ...withoutSubTypes, event } as T));
    return acc;
  }, []);
};

// The backend expects this full set on every twelve-volt-battery registration.
// The "12-volt battery" checkbox is all-or-nothing, so there is no per-sub-type UI.
export const TWELVE_VOLT_BATTERY_SUBTYPES = [
  'SAC001',
  'SAC002',
  'SAC003',
  'SAC004',
  'SAC005',
] as const;

/**
 * Expands the canonical `low-battery` selection back into both backend events, which
 * is what the backend expects whenever the "12-volt battery" checkbox is enabled.
 * @param isVehicleHealthEnabled - When off, `twelve-volt-battery` is never emitted.
 */
export const expandTwelveVoltBatteryRegistrations = <
  T extends { category: string; event: string; subTypes?: string[] },
>(
  registrations: T[],
  isVehicleHealthEnabled: boolean
): T[] =>
  // Collapsing first keeps this idempotent when re-submitting an already expanded payload.
  collapseTwelveVoltBatteryEvents(
    registrations,
    isVehicleHealthEnabled
  ).flatMap((registration) =>
    isVehicleHealthEnabled && registration.event === DiagnosticEvent.LOW_BATTERY
      ? [
          registration,
          {
            ...registration,
            event: DiagnosticEvent.TWELVE_VOLT_BATTERY,
            subTypes: [...TWELVE_VOLT_BATTERY_SUBTYPES],
          } as T,
        ]
      : [registration]
  );

export const getNotificationFormState = (
  notification?: Partial<AvailableSubscription>
): NotificationFormState => {
  return getFormAsState({
    // Determine if notification is active based on priority:
    // 1. If toggleActive is explicitly false, notification is inactive
    // 2. If active property is a boolean, use that value
    // 3. Otherwise, if  custom  notification  then inactive, if no name or empty then active
    active:
      notification?.toggleActive === false
        ? false
        : isBoolean(notification?.active)
          ? notification?.active
          : !notification?.name?.trim(),
    name: notification?.preferredName || notification?.name || null,
    receiptPreferences: notification?.receiptPreferences || null,
    selectedEvents:
      notification?.eventOptions?.sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      ) || null,
    speedThreshold: notification?.speedThreshold || null,
    selectedSubTypes: notification?.selectedSubTypes || null,
    selectedAssets: notification?.selectedAssets || null,
    selectedVehicleSubscriberType:
      notification?.selectedVehicleSubscriberType || null,
    grouped: notification?.grouped || false,
    emailFrequency: notification?.emailFrequency || EmailFrequency.IMMEDIATELY,
    selectedSensitivity: notification?.selectedSensitivity || 'MEDIUM',
    // Default to 10 minutes for vehicle idling and 10 days for vehicle inactivity
    vehicleIdling:
      notification?.vehicleIdling ?? VEHICLE_IDLING_DEFAULT_DURATION,
    vehicleInactivity:
      notification?.vehicleInactivity ?? VEHICLE_INACTIVITY_DEFAULT_DURATION,
    selectedDays: notification?.selectedDays ?? VEHICLE_OFF_HOURS_DEFAULT_DAYS,
    // When isAllDay is true, use default times so unchecking "All day" shows valid range (no 00:00/00:00)
    selectedStartTime: notification?.isAllDay
      ? VEHICLE_OFF_HOURS_DEFAULT_START_TIME
      : (notification?.selectedStartTime ??
        VEHICLE_OFF_HOURS_DEFAULT_START_TIME),
    selectedEndTime: notification?.isAllDay
      ? VEHICLE_OFF_HOURS_DEFAULT_END_TIME
      : (notification?.selectedEndTime ?? VEHICLE_OFF_HOURS_DEFAULT_END_TIME),
    isAllDay: notification?.isAllDay ?? false,
  } as NotificationCardFormFields);
};

export const getSuccessMessageTitle = (
  subscription: AvailableSubscription | undefined,
  title: string,
  isVehicleHealthEnabled = false
) => {
  if (!subscription) return title;

  const { category, event } = subscription;

  switch (category) {
    case AlertCategory.PROFILE:
      return event === AlertEvent.ACCOUNT_UPDATE
        ? translateText(
            'settings:notifications.vehicles.activityNotifications.title_singular'
          )
        : translateText('settings:account.user_singular');

    case AlertCategory.DIAGNOSTIC:
      return translateText(
        getVehicleHealthCopyKeys(isVehicleHealthEnabled).sectionTitleSingular
      );

    case AlertCategory.ASSET:
      return translateText(
        'settings:notifications.vehicles.activityNotifications.title_singular'
      );

    default:
      return title;
  }
};

/** Calendar/clock fields of `date` as seen in `timeZone`. */
const partsInZone = (date: Date, timeZone: string): Record<string, string> =>
  Object.fromEntries(
    // biome-ignore lint/plugin/no-direct-intl: zone wall-clock parts for schedule math; core has no equivalent
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  );

/** UTC ms for wall-clock `h`:`m` on `date`'s calendar day in `timeZone`. */
const wallClockToUtcMs = (
  timeZone: string,
  h: number,
  m: number,
  date = new Date()
): number => {
  const p = partsInZone(date, timeZone);
  const desiredAsUtc = Date.UTC(+p.year, +p.month - 1, +p.day, h, m, 0);
  const asUtc = (ms: number) => {
    const q = partsInZone(new Date(ms), timeZone);
    return Date.UTC(
      +q.year,
      +q.month - 1,
      +q.day,
      +q.hour,
      +q.minute,
      +q.second
    );
  };
  let ms = desiredAsUtc;
  ms -= asUtc(ms) - ms;
  ms -= asUtc(ms) - desiredAsUtc; // second pass for DST
  return ms;
};

/** ISO weekday name: 1 = Monday … 7 = Sunday (2024-01-01 is a Monday). */
const weekdayName = (day: number, locale: string): string =>
  // biome-ignore lint/plugin/no-direct-intl: weekday name lookup by fixed UTC epoch day; core has no equivalent
  new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2024, 0, day))
  );

/**
 * The instant the email digest goes out: 8:00 AM Eastern, on the Eastern day in progress.
 *
 * An instant rather than a wall-clock reading, so each reader's zone can name its own hour for it.
 * Eastern is the zone the schedule is defined in, and it carries its own EST/EDT transition.
 *
 * @returns The send time as an absolute instant.
 */
export const getEmailScheduleInstant = (): Date =>
  new Date(wallClockToUtcMs(TIMEZONE_NEW_YORK, 8, 0));

/**
 * Legacy flag-off schedule reading: 8:00 AM Eastern as a fixed 12-hour string in
 * `userTimeZone`. Does not follow reader hourCycle prefs — those apply only on the
 * localization-on path that formats through `format.time`.
 */
export const getDefaultEmailScheduleInLocalTimeFormat = (
  userTimeZone: string
): TimeWithTimezone => {
  const { preferences } = resolvePreferences({
    userPreferences: { locale: 'en-US', timeZone: userTimeZone },
  });
  // 8:00 AM America/New_York → wall time in the reader's resolved zone.
  const instant = getEmailScheduleInstant();
  const { hour, minute } = partsInZone(instant, preferences.timeZone);
  const hourNumber = Number(hour);
  const displayHours = hourNumber % 12 || 12;
  const period = hourNumber >= 12 ? 'PM' : 'AM';

  return {
    time: `${displayHours}:${minute.padStart(2, '0')} ${period}`,
    timezone: formatZoneName(preferences, instant),
  };
};

const cameraAlertsOrder = [
  String(CamerasSettingType.IN_CAB_ALERT_INATTENTIVE),
  String(CamerasSettingType.IN_CAB_ALERT_EATING_OR_DRINKING),
  String(CamerasSettingType.IN_CAB_ALERT_LENS_OBSTRUCTED),
  String(CamerasSettingType.IN_CAB_ALERT_PHONE_USE),
  String(CamerasSettingType.IN_CAB_ALERT_SEATBELT_UNBUCKLED),
  String(CamerasSettingType.IN_CAB_ALERT_SMOKING),
];

export const mapCameraSettingsToAvailableSubscription = (
  settings: CamerasSetting[]
): AvailableSubscription[] => {
  const mappedSettings = settings.map((setting) => {
    const availableSubScriptionSetting = {
      active: setting.details.enabled ?? false,
      subscriptionId: String(setting.name),
      category: 'cameraAlerts',
      event: String(setting.name),
      eventOptions: [] as SubscriptionEvent[],
      name: translateText(
        `settings:notification.cameras.inCabAlerts.${setting.name}`
      ),
      selectId: `cameraAlerts*${String(setting.name)}`,
      id: String(setting.name),
      pinValue: setting.details.value ?? '3333',
      speedThreshold: setting.details.minSpeedThresholdInMPH ?? '10',
      receiptPreferences: [],
    } as AvailableSubscription;

    return availableSubScriptionSetting;
  });

  const cameraAlertsOrderMap = Object.fromEntries(
    cameraAlertsOrder.map((alert, index) => [alert, index])
  );
  mappedSettings.sort(
    (a, b) => cameraAlertsOrderMap[a.event] - cameraAlertsOrderMap[b.event]
  );

  return mappedSettings;
};

/**
 * The sentence naming when the email digest is sent.
 *
 * The hour and the zone arrive already rendered, because who renders them differs: the reader's own
 * formatters once localization is on, and {@link getDefaultEmailScheduleInLocalTimeFormat} until
 * then. Only the weekday is named here, since neither path formats one.
 *
 * @param frequency - Which digest. `IMMEDIATELY` has no schedule and yields an empty string.
 * @param schedule - The send time and the name of the zone it is read in.
 * @param currentLanguage - The locale the weekday is named in.
 * @param selectedDay - The Luxon weekday the weekly and biweekly digests land on. Defaults to
 *   Monday.
 * @returns The sentence, such as `Sent daily at 8:00 AM EDT`.
 */
export const getEmailDeliveryScheduleMessage = (
  frequency: EmailFrequency,
  schedule: TimeWithTimezone,
  currentLanguage: string,
  selectedDay?: number
): string => {
  const { time, timezone: timezoneAbbr } = schedule;
  switch (frequency) {
    case EmailFrequency.DAILY:
      return translateText('asset:notification.emailDelivery.daily', {
        time: time,
        timezone: timezoneAbbr,
      });

    case EmailFrequency.WEEKLY:
      return translateText('asset:notification.emailDelivery.weekly', {
        day: weekdayName(selectedDay || 1, currentLanguage),
        time: time,
        timezone: timezoneAbbr,
      });

    case EmailFrequency.BIWEEKLY:
      return translateText('asset:notification.emailDelivery.biweekly', {
        day: weekdayName(selectedDay || 1, currentLanguage),
        time: time,
        timezone: timezoneAbbr,
      });

    default:
      return '';
  }
};

export const categoryIconMap: Record<string, string> = {
  [AlertCategory.DIAGNOSTIC]: iconMap.vehicle,
  [AlertCategory.PROFILE]: iconMap.user,
  [AlertCategory.ASSET]: iconMap.vehicle,
  default: iconMap.default,
};

export const getNotificationIcon = (
  event?: string,
  category?: string
): string => {
  if (event && iconMap[event]) {
    return iconMap[event];
  }

  if (category && categoryIconMap[category]) {
    return categoryIconMap[category];
  }

  return categoryIconMap.default;
};

export type RuleEntry = {
  rule: RuleItemResponse['rule'];
  ruleId: string;
};

/**
 * Builds a lookup from rules by category+event and returns a getter.
 * @param rules - List of rule responses (e.g. from getRuleListByCategoryAndEvent)
 * @returns getSubscriptionRuleEntry(category, event) returning RuleEntry | undefined
 */
export const buildSubscriptionRuleEntryLookup = (
  rules: RuleItemResponse[] = []
): ((category: string, event: string) => RuleEntry | undefined) => {
  const ruleByCategoryEvent = rules.reduce(
    (acc: Record<string, RuleEntry>, rule) => {
      const key = `${rule.category}:${rule.event}`;
      if (rule.rule) acc[key] = { rule: rule.rule, ruleId: rule.id };
      return acc;
    },
    {}
  );
  return (category: string, event: string) =>
    ruleByCategoryEvent[`${category}:${event}`] ?? undefined;
};

/**
 * Whether a group's switch is off: it has rows and none of them has the group
 * toggle on. The list page shows every row of such a group as off.
 * @param subscriptions - The rows of one group
 * @returns True when the group's switch is off
 */
export const isGroupToggleOff = (
  subscriptions: Pick<AvailableSubscription, 'toggleActive'>[]
): boolean =>
  subscriptions.length > 0 &&
  !subscriptions.some((subscription) => subscription.toggleActive === true);
